import { format, isValid } from "date-fns";
import { Calendar, ToggleLeft, ToggleRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDisplayDate } from "@/lib/dateUtils";
import {
  formatLifecycleContext,
  getReactivationSuggestions,
} from "@/lib/lifecycleUtils";
import { getPaymentContext } from "@/lib/utils";
import { type Subscription } from "@/types/subscription";

interface PaymentInformationProps {
  subscription: Subscription;
  showLastPayment: boolean;
  setShowLastPayment: (show: boolean) => void;
  derivedNext: Date | null;
  daysUntil: number;
  isOverdue: boolean;
  isUrgent: boolean;
  formatLastPayment: (payment: any) => {
    text: string;
    amount: string;
    shortText: string;
  };
}

export const PaymentInformation = ({
  subscription,
  showLastPayment,
  setShowLastPayment,
  derivedNext,
  daysUntil,
  isOverdue,
  isUrgent,
  formatLastPayment,
}: PaymentInformationProps) => {
  // Format next payment date with proper validation
  const formatNextPayment = () => {
    if (!derivedNext || !isValid(derivedNext)) {
      // For PAYG without schedule, show appropriate message
      if (
        subscription.billingType === "pay_as_you_go" &&
        !subscription.frequency
      ) {
        return "No fixed schedule";
      }
      return "—"; // Generic fallback
    }
    // Use standardized display formatter that preserves intended calendar day for date-only values
    return formatDisplayDate(derivedNext);
  };

  return (
    <div className="mb-4 space-y-3">
      {/* Toggle Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {showLastPayment ? "Last Payment" : "Next Payment"}
          </span>
        </div>

        {getPaymentContext(subscription).hasHistory &&
          subscription.status !== "cancelled" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLastPayment(!showLastPayment)}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-card-foreground"
            >
              {showLastPayment ? (
                <ToggleRight className="h-3 w-3" />
              ) : (
                <ToggleLeft className="h-3 w-3" />
              )}
              Toggle
            </Button>
          )}
      </div>

      {/* Payment Information Display */}
      <div className="flex items-center justify-between">
        <div>
          {subscription.status === "cancelled" ? (
            <div className="space-y-2">
              {/* Enhanced Lifecycle Context for Cancelled Subscriptions */}
              {(() => {
                const lifecycleContext = formatLifecycleContext(subscription);
                const reactivationSuggestion =
                  getReactivationSuggestions(subscription);

                return (
                  <div className="space-y-1">
                    {/* Primary: Lifecycle Context */}
                    <div>
                      <span className="text-sm text-card-foreground">
                        {lifecycleContext.primary}
                      </span>
                      {lifecycleContext.badge && (
                        <Badge
                          variant="secondary"
                          className="ml-2 px-2 py-0 text-xs"
                        >
                          {lifecycleContext.badge}
                        </Badge>
                      )}
                    </div>

                    {/* Secondary Information */}
                    <div className="space-y-1">
                      {lifecycleContext.secondary && (
                        <span className="block text-xs text-muted-foreground">
                          {lifecycleContext.secondary}
                        </span>
                      )}
                      {subscription.cancellationDate && (
                        <span className="block text-xs text-muted-foreground">
                          Cancelled on{" "}
                          {format(
                            new Date(subscription.cancellationDate),
                            "MMM dd, yyyy",
                          )}
                        </span>
                      )}
                      {reactivationSuggestion && (
                        <span
                          className={`block text-xs ${
                            reactivationSuggestion.urgency === "high"
                              ? "text-primary"
                              : reactivationSuggestion.urgency === "medium"
                                ? "text-orange-600"
                                : "text-muted-foreground"
                          }`}
                        >
                          💡 {reactivationSuggestion.suggestion}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : showLastPayment && getPaymentContext(subscription).lastPayment ? (
            <div className="space-y-1">
              <span className="text-sm text-card-foreground">
                {
                  formatLastPayment(getPaymentContext(subscription).lastPayment)
                    .text
                }
              </span>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">
                  {
                    formatLastPayment(
                      getPaymentContext(subscription).lastPayment,
                    ).amount
                  }
                </span>
                <Badge variant="outline" className="px-1 py-0 text-xs">
                  {getPaymentContext(subscription).paymentCount} payments
                </Badge>
              </div>
            </div>
          ) : (
            <span className="text-sm text-card-foreground">
              {formatNextPayment()}
            </span>
          )}
        </div>

        {/* Due Status */}
        {subscription.status !== "cancelled" &&
          !showLastPayment &&
          derivedNext &&
          isValid(derivedNext) && (
            <div
              className={`text-sm font-medium ${
                isOverdue
                  ? "text-destructive"
                  : isUrgent
                    ? "text-warning"
                    : "text-muted-foreground"
              }`}
            >
              {isOverdue
                ? `${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? "s" : ""} overdue`
                : daysUntil === 0
                  ? "Due today"
                  : `in ${daysUntil} day${daysUntil !== 1 ? "s" : ""}`}
            </div>
          )}
      </div>
    </div>
  );
};
